/**
 * activityProfileConfig.js
 * ─────────────────────────────────────────────────────────────────────────────
 * 5 profils + architecture certificationFramework
 *
 * certificationFramework structure :
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ primary        — certification principale (clé de routage PDF)          │
 * │ secondary[]    — certifications complémentaires affichées               │
 * │ financeurs[]   — organismes de financement applicables                  │
 * │ pdfGenerator   — clé du générateur PDF à instancier (CertificationPDF)  │
 * │ indicateurs[]  — référentiels qualité affichés dans l'UI                │
 * │ mentions       — texte légal bas de page du PDF                         │
 * │ saleArguments[]— arguments pré-remplis pour le CRM / pitch commercial   │
 * └─────────────────────────────────────────────────────────────────────────┘
 */

export const PROFILES = {

  // ── 1. ÉDUCATION / FORMATION ──────────────────────────────────────────────
  education: {
    id:          'education',
    label:       'École & Formation',
    description: 'Établissements, CFA, organismes de formation',
    icon:        '🎓',
    accentColor: 'orange',

    personas: {
      label: "Type d'organisme",
      description: "Sélectionnez votre catégorie pour adapter le tableau de bord et les documents réglementaires.",
      required: true,
      tree: [
        {
          key: 'centre-formation',
          label: 'Centre de formation',
          children: [
            { key: 'cfa', label: 'CFA' },
            { key: 'organisme-prive', label: 'Organisme privé' },
            { key: 'organisme-public', label: 'Organisme public' },
          ]
        },
        {
          key: 'etablissement-scolaire',
          label: 'Établissement scolaire',
          children: [
            { key: 'lycee-pro', label: 'Lycée professionnel' },
            { key: 'universite', label: 'Université' },
            { key: 'ecole-ingenieur', label: 'École d\'ingénieur' },
          ]
        },
        {
          key: 'formateur-independant',
          label: 'Formateur indépendant',
          children: [
            { key: 'auto-entrepreneur',   label: 'Auto-entrepreneur' },
            { key: 'societe-unipersonnelle', label: 'Société unipersonnelle (EURL/SASU)' },
            { key: 'portage-salarial',    label: 'Portage salarial' },
          ]
        },
        {
          key: 'plateforme-elearning',
          label: 'Plateforme e-learning',
          children: [
            { key: 'mooc-cooc',          label: 'MOOC / COOC' },
            { key: 'lms-proprietaire',   label: 'LMS propriétaire' },
            { key: 'marketplace-formation', label: 'Marketplace formation' },
          ]
        },
        {
          key: 'autre-education',
          label: 'Autre',
          children: []
        }
      ]
    },

    onboarding: {
      title: 'Informations établissement (Qualiopi)',
      description: 'Ces données figureront sur vos relevés Qualiopi et sont obligatoires pour la certification.',
      fields: [
        // Champs existants
        { key: 'establishmentName', label: 'Nom de l’établissement', type: 'text', required: true },
        { key: 'siret', label: 'Numéro SIRET', type: 'text', required: false },

        // Nouveaux champs Qualiopi
        { key: 'declarationActivite', label: 'Numéro de déclaration d’activité (NDA)', type: 'text', required: false },
        { key: 'dateObtentionQualiopi', label: 'Date d’obtention de la certification Qualiopi', type: 'date', required: false },
        { key: 'certificateur', label: 'Organisme certificateur', type: 'text', required: false },
        { key: 'dateFinCertification', label: 'Date de fin de validité de la certification', type: 'date', required: false },

        // Contact responsable Qualité
        { key: 'responsableQualite', label: 'Responsable Qualité', type: 'text', required: false },
        { key: 'emailResponsable', label: 'Email du responsable Qualité', type: 'email', required: false },

        // Accords obligatoires
        { key: 'acceptQualiopi', label: 'Je déclare respecter les critères du Référentiel National Qualité (Qualiopi)', type: 'checkbox', required: true },
        { key: 'acceptRGPD', label: 'Je déclare être en conformité avec le RGPD pour les données des apprenants', type: 'checkbox', required: true },
      ],
    },
  

    // ── Certification & financement ─────────────────────────────────────────
    certificationFramework: {
      primary:   'qualiopi',
      secondary: ['rncp', 'rs'],         // Répertoire National des Certifications Professionnelles / Répertoire Spécifique
      pdfGenerator: 'QualiopiPDF',       // clé → CertificationPDF.js router
      indicateurs: [
        { code: 'IND-1',  label: 'Conditions d\'information du public'          },
        { code: 'IND-2',  label: 'Identification des objectifs de formation'    },
        { code: 'IND-4',  label: 'Adaptation aux publics bénéficiaires'         },
        { code: 'IND-6',  label: 'Inscription dans l\'environnement professionnel' },
        { code: 'IND-7',  label: 'Recueil des appréciations et résultats'       },
      ],
      financeurs: [
        { id: 'cpf',         label: 'CPF',             description: 'Compte Personnel de Formation',          url: 'https://www.moncompteformation.gouv.fr' },
        { id: 'opco',        label: 'OPCO',            description: 'Opérateurs de Compétences',              url: 'https://www.francecompetences.fr'         },
        { id: 'pole_emploi', label: 'France Travail',  description: 'Financement demandeurs d\'emploi',       url: 'https://www.francetravail.fr'             },
        { id: 'region',      label: 'Région',          description: 'Plans régionaux de formation',           url: null                                       },
        { id: 'agefiph',     label: 'Agefiph',         description: 'Financement travailleurs handicapés',    url: 'https://www.agefiph.fr'                   },
      ],
      mentions: 'Ce relevé est établi conformément aux exigences du Référentiel National Qualité (RNQ) Qualiopi. '
              + 'Il atteste des formations réalisées et peut être présenté lors d\'audits de certification, '
              + 'auprès des organismes financeurs (OPCO, CPF, France Travail) ou de tout contrôle administratif.',
      saleArguments: [
        'Éligible CPF — formation finançable sans reste à charge',
        'Attestations Qualiopi auto-générées à chaque fin de parcours',
        'Dossiers OPCO pré-remplis depuis la plateforme',
        'Suivi indicateurs 1, 2, 4, 6, 7 en temps réel',
      ],
    },

    layout: {
      homeModule:   'dashboard',
      sidebarOrder: ['dashboard', 'inscriptions', 'protocoles', 'cohortes', 'crm', 'contact', 'personnel', 'branding', 'stats', 'modération'],
      modules: {
        dashboard:    { icon: 'LayoutDashboard', label: 'Tableau de Bord',  source: 'dashboard',    renderMode: 'cosmetic'                            },
        inscriptions: { icon: 'Users',           label: 'Apprenants',       source: 'inscriptions', renderMode: 'cosmetic'                            },
        protocoles:   { icon: 'BookOpen',        label: 'Protocoles',       source: 'protocoles',   renderMode: 'cosmetic'                            },
        cohortes:     { icon: 'Grid',            label: 'Cohortes',         source: 'cohortes',     renderMode: 'cosmetic'                            },
        crm:          { icon: 'Kanban',          label: 'CRM',              source: 'crm',          renderMode: 'specialized', component: 'CRMPanel'  },
        contact:      { icon: 'MessageSquare',   label: 'Messagerie',       source: 'contact',      renderMode: 'cosmetic'                            },
        personnel:    { icon: 'UserCheck',       label: 'Formateurs',       source: 'personnel',    renderMode: 'cosmetic'                            },
        branding:     { icon: 'Palette',         label: 'Branding',         source: 'branding',     renderMode: 'cosmetic'                            },
        stats:        { icon: 'BarChart',        label: 'Statistiques',     source: 'stats',        renderMode: 'cosmetic'                            },
        modération:   { icon: 'Shield',          label: 'Modération',       source: 'modération',   renderMode: 'cosmetic'                            },
      },
    },

    vocabulary: {
      learners:        'Apprenants',
      learner:         'Apprenant',
      courses:         'Parcours',
      course:          'Parcours',
      cohortes:        'Cohortes',
      cohorte:         'Cohorte',
      staff:           'Formateurs',
      staffMember:     'Formateur',
      enroll:          'Inscrire',
      complete:        'Terminer',
      progress:        'Progression',
      sectionLearners: 'APPRENANTS',
      sectionCourses:  'PARCOURS',
      sectionCohortes: 'COHORTES',
      sectionStaff:    'FORMATEURS',
      sectionCRM:      'CRM',
      contact:         'Prospect',
      contacts:        'Prospects',
      pipeline:        "Pipeline d'inscriptions",
      deal:            "Dossier d'admission",
      company:         'Établissement',
    },

    kpis: {
      total:      'Apprenants total',
      active:     'Actifs',
      revenue:    'Pesas circulants',
      completion: 'Taux de complétion',
    },

    crmConfig: {
      pipelineStages: [
        { id: 'prospect', label: 'Prospect', color: 'zinc',    icon: '👀' },
        { id: 'candidat', label: 'Candidat', color: 'sky',     icon: '📋' },
        { id: 'inscrit',  label: 'Inscrit',  color: 'amber',   icon: '✍️' },
        { id: 'actif',    label: 'Actif',    color: 'emerald', icon: '🎯' },
        { id: 'diplome',  label: 'Diplômé',  color: 'purple',  icon: '🏆' },
        { id: 'perdu',    label: 'Perdu',    color: 'red',     icon: '✗'  },
      ],
      contactFields: ['nom', 'email', 'telephone', 'etablissement', 'protocoles_interesse', 'budget'],
      scoreLabel:   "Score d'admission",
      noteLabel:    'Note de suivi',
    },

    sections: {
      dashboard: true, contact: true, protocoles: true, inscriptions: true,
      cohortes: true, personnel: true, branding: true, stats: true,
      modération: true, crm: true,
    },
  },

  // ── 2. ENTREPRISE / CORPORATE / SAAS ─────────────────────────────────────
  corporate: {
    id:          'corporate',
    label:       'Entreprise & Corporate',
    description: 'RH, L&D interne, SaaS, industrie, formation équipes',
    icon:        '🏛️',
    accentColor: 'sky',

    personas: {
      label: "Type d'entreprise",
      description: "Précisez votre secteur et taille pour les documents OPCO et le suivi RH.",
      required: true,
      tree: [
        {
          key: 'pme',
          label: 'PME (< 250 salariés)',
          children: [
            { key: 'pme-industrie', label: 'Industrie' },
            { key: 'pme-services', label: 'Services' },
            { key: 'pme-commerce', label: 'Commerce' },
          ]
        },
        {
          key: 'eti',
          label: 'ETI (250 – 5000 salariés)',
          children: [
            { key: 'eti-multisites',     label: 'Multi-sites France' },
            { key: 'eti-international',  label: 'Groupe international' },
            { key: 'eti-holding',        label: 'Holding' },
          ]
        },
        {
          key: 'grand-groupe',
          label: 'Grand groupe (> 5000)',
          children: [
            { key: 'siege',              label: 'Siège social' },
            { key: 'bu-division',        label: 'BU / Division' },
            { key: 'filiale',            label: 'Filiale' },
          ]
        },
        {
          key: 'startup',
          label: 'Startup / Scale-up',
          children: [
            { key: 'seed',               label: 'Seed / Amorçage' },
            { key: 'serie-ab',           label: 'Série A–B' },
            { key: 'late-stage',         label: 'Late stage' },
          ]
        },
        {
          key: 'autre-corporate',
          label: 'Autre',
          children: []
        }
      ]
    },

    onboarding: {
      title: 'Informations entreprise (Qualiopi)',
      description: 'Ces données seront utilisées pour les documents Qualiopi et les dossiers de financement.',
      fields: [
        // Champs existants
        { key: 'companyName', label: 'Raison sociale', type: 'text', required: true },
        { key: 'siret', label: 'Numéro SIRET', type: 'text', required: false },

        // Nouveaux champs spécifiques Corporate
        { key: 'effectif', label: 'Effectif total', type: 'number', required: false },
        { key: 'conventionCollective', label: 'Convention collective applicable', type: 'text', required: false },
        { key: 'opcoRattachement', label: 'OPCO de rattachement', type: 'select', options: ['AKTO', 'OPCO 2i', 'OPCO EP', 'AFDAS', 'Autre'], required: false },

        // Responsable formation
        { key: 'contactName', label: 'Responsable formation', type: 'text', required: false },
        { key: 'emailResponsable', label: 'Email du responsable formation', type: 'email', required: false },

        // Accords
        { key: 'acceptQualiopi', label: 'Je déclare respecter les critères Qualiopi', type: 'checkbox', required: true },
        { key: 'acceptRGPD', label: 'Je déclare être en conformité avec le RGPD pour les données des collaborateurs', type: 'checkbox', required: true },
      ],
    },

    certificationFramework: {
      primary:   'qualiopi',
      secondary: ['iso9001', 'rncp'],
      pdfGenerator: 'QualiopiPDF',
      indicateurs: [
        { code: 'IND-1',  label: 'Information et accès au public'              },
        { code: 'IND-2',  label: 'Objectifs et compétences ciblées'            },
        { code: 'IND-4',  label: 'Moyens pédagogiques et techniques'           },
        { code: 'IND-6',  label: 'Insertion dans l\'environnement pro'         },
        { code: 'IND-7',  label: 'Évaluation de la satisfaction et des acquis' },
      ],
      financeurs: [
        { id: 'opco',    label: 'OPCO',                description: 'Plan de développement des compétences',     url: 'https://www.francecompetences.fr'  },
        { id: 'fne',     label: 'FNE-Formation',        description: 'Chômage partiel / Transitions pro',        url: 'https://travail-emploi.gouv.fr'    },
        { id: 'cpf_co',  label: 'CPF de transition',    description: 'Projet de Transition Professionnelle',     url: 'https://www.transitionspro.fr'     },
        { id: 'agefiph', label: 'Agefiph',              description: 'Financement travailleurs handicapés',       url: 'https://www.agefiph.fr'            },
      ],
      mentions: 'Ce document est établi conformément aux exigences Qualiopi et au plan de développement '
              + 'des compétences de l\'entreprise. Il peut être présenté à l\'OPCO compétent ou lors '
              + 'd\'un contrôle URSSAF relatif aux dépenses de formation.',
      saleArguments: [
        'Imputabilité OPCO — dossiers de prise en charge pré-formatés',
        'Reporting plan de formation exportable pour le CSE',
        'Suivi individuel par collaborateur conforme bilan social',
        'Éligible FNE-Formation en cas de chômage partiel',
      ],
    },

    layout: {
      homeModule:   'dashboard',
      sidebarOrder: ['dashboard', 'inscriptions', 'protocoles', 'cohortes', 'crm', 'contact', 'personnel', 'stats', 'branding', 'modération'],
      modules: {
        dashboard:    { icon: 'LayoutDashboard', label: 'Dashboard',       source: 'dashboard',    renderMode: 'cosmetic'                                    },
        inscriptions: { icon: 'Users',           label: 'Collaborateurs',  source: 'inscriptions', renderMode: 'cosmetic'                                    },
        protocoles:   { icon: 'BookOpen',        label: 'Programmes',      source: 'protocoles',   renderMode: 'cosmetic'                                    },
        cohortes:     { icon: 'Grid',            label: 'Équipes',         source: 'cohortes',     renderMode: 'cosmetic'                                    },
        crm:          { icon: 'Kanban',          label: 'CRM',             source: 'crm',          renderMode: 'specialized', component: 'CRMCorporatePanel' },
        contact:      { icon: 'MessageSquare',   label: 'Messagerie',      source: 'contact',      renderMode: 'cosmetic'                                    },
        personnel:    { icon: 'UserCheck',       label: 'Équipe',          source: 'personnel',    renderMode: 'cosmetic'                                    },
        stats:        { icon: 'BarChart',        label: 'Analytics',       source: 'stats',        renderMode: 'cosmetic'                                    },
        branding:     { icon: 'Palette',         label: 'Marque',          source: 'branding',     renderMode: 'cosmetic'                                    },
        modération:   { icon: 'Shield',          label: 'Conformité',      source: 'modération',   renderMode: 'cosmetic'                                    },
      },
    },

    vocabulary: {
      learners:        'Collaborateurs',
      learner:         'Collaborateur',
      courses:         'Programmes',
      course:          'Programme',
      cohortes:        'Équipes',
      cohorte:         'Équipe',
      staff:           'Managers',
      staffMember:     'Manager',
      enroll:          'Affecter',
      complete:        'Valider',
      progress:        'Progression',
      sectionLearners: 'COLLABORATEURS',
      sectionCourses:  'PROGRAMMES',
      sectionCohortes: 'ÉQUIPES',
      sectionStaff:    'MANAGERS',
      sectionCRM:      'CRM',
      contact:         'Lead',
      contacts:        'Leads',
      pipeline:        'Pipeline de vente',
      deal:            'Opportunité',
      company:         'Compte',
    },

    kpis: {
      total:      'Collaborateurs total',
      active:     'Actifs',
      revenue:    'MRR',
      completion: "Taux d'activation",
    },

    crmConfig: {
      pipelineStages: [
        { id: 'lead',      label: 'Lead',           color: 'zinc',    icon: '📡' },
        { id: 'qualified', label: 'Qualifié',        color: 'sky',     icon: '🔍' },
        { id: 'demo',      label: 'Démo planifiée',  color: 'amber',   icon: '📅' },
        { id: 'trial',     label: 'En essai',        color: 'purple',  icon: '⚗️' },
        { id: 'closed',    label: 'Client',          color: 'emerald', icon: '✅' },
        { id: 'churned',   label: 'Churné',          color: 'red',     icon: '↩️' },
      ],
      contactFields: ['nom', 'email', 'entreprise', 'taille_equipe', 'plan_interesse', 'arr_estime'],
      scoreLabel:   'Lead Score',
      noteLabel:    'Notes de qualification',
    },

    sections: {
      dashboard: true, contact: true, protocoles: true, inscriptions: true,
      cohortes: true, personnel: true, branding: true, stats: true,
      modération: true, crm: true,
    },
  },

  // ── 3. CRÉATION / MÉDIA ───────────────────────────────────────────────────
  creative: {
    id:          'creative',
    label:       'Création & Média',
    description: 'Agences, studios, freelances, production audiovisuelle',
    icon:        '🎬',
    accentColor: 'fuchsia',

    onboarding: {
      title: 'Informations structure (Qualiopi / AFDAS)',
      description: 'Données pour les attestations et les demandes de financement AFDAS.',
      fields: [
        { key: 'structureName', label: 'Nom de la structure', type: 'text', required: true },
        { key: 'siret', label: 'Numéro SIRET', type: 'text', required: false },
        { key: 'afdasId', label: 'Identifiant AFDAS (si connu)', type: 'text', required: false },
        { key: 'acceptQualiopi', label: 'Je déclare respecter les critères Qualiopi', type: 'checkbox', required: true },
        { key: 'secteur', label: 'Secteur d’activité', type: 'select', options: ['Audiovisuel', 'Cinéma', 'Spectacle vivant', 'Édition', 'Presse', 'Publicité', 'Autre'], required: false },
      ],
    },

    personas: {
      label: "Type de structure",
      description: "Précisez votre catégorie pour adapter les modules et les dossiers AFDAS.",
      required: true,
      tree: [
        {
          key: 'agence-creative',
          label: 'Agence créative',
          children: [
            { key: 'agence-com', label: 'Agence de communication' },
            { key: 'agence-pub', label: 'Agence publicitaire' },
            { key: 'studio-design', label: 'Studio de design' },
          ]
        },
        {
          key: 'studio-audiovisuel',
          label: 'Studio audiovisuel',
          children: [
            { key: 'studio-production', label: 'Production cinéma / TV' },
            { key: 'studio-podcast', label: 'Podcast & radio' },
            { key: 'studio-jeu', label: 'Jeu vidéo & animation' },
          ]
        },
        {
          key: 'freelance-creatif',
          label: 'Freelance créatif',
          children: [
            { key: 'photographe', label: 'Photographe' },
            { key: 'motion-designer', label: 'Motion designer' },
            { key: 'illustrateur', label: 'Illustrateur / Graphiste' },
          ]
        },
        {
          key: 'production-presse',
          label: 'Production & Presse',
          children: [
            { key: 'maison-edition', label: "Maison d'édition" },
            { key: 'redaction', label: 'Rédaction / Média en ligne' },
            { key: 'intermittent', label: 'Intermittent du spectacle' },
          ]
        },
        {
          key: 'autre-creative',
          label: 'Autre',
          children: []
        }
      ]
    },

    certificationFramework: {
      primary:   'qualiopi',
      secondary: ['afdas_agrement', 'rs'],
      pdfGenerator: 'QualiopiPDF',
      indicateurs: [
        { code: 'IND-1',  label: 'Information des bénéficiaires'               },
        { code: 'IND-2',  label: 'Objectifs et compétences créatives'          },
        { code: 'IND-4',  label: 'Moyens pédagogiques (ateliers, production)'  },
        { code: 'IND-7',  label: 'Résultats et satisfaction'                   },
      ],
      financeurs: [
        { id: 'afdas',       label: 'AFDAS',           description: 'OPCO Audiovisuel, Presse, Culture, Sport',  url: 'https://www.afdas.com'             },
        { id: 'cpf',         label: 'CPF',             description: 'Compte Personnel de Formation',             url: 'https://www.moncompteformation.gouv.fr' },
        { id: 'intermittent',label: 'Audiens',         description: 'Formation intermittents du spectacle',      url: 'https://www.audiens.org'           },
        { id: 'cnc',         label: 'CNC',             description: 'Centre National du Cinéma — aides formation',url: 'https://www.cnc.fr'               },
        { id: 'region',      label: 'Région',          description: 'Fonds régionaux culture & création',        url: null                                },
      ],
      mentions: 'Ce relevé est établi conformément aux exigences Qualiopi et peut être présenté à l\'AFDAS '
              + '(OPCO des secteurs Audiovisuel, Communication, Presse, Culture et Sport) '
              + 'ou à tout organisme financeur de la formation dans les industries créatives.',
      saleArguments: [
        'Financement AFDAS — OPCO dédié aux intermittents et créatifs',
        'Éligible CNC pour les formations liées à la production audiovisuelle',
        'Attestations adaptées aux dossiers intermittents du spectacle',
        'Suivi des heures de formation valorisable dans le cachet artistique',
      ],
    },

    layout: {
      homeModule:   'contacts',
      sidebarOrder: ['dashboard', 'contacts', 'projets', 'profils', 'collectifs', 'portfolio', 'staff', 'messagerie', 'stats', 'modération'],
      modules: {
        dashboard:  { icon: 'LayoutDashboard', label: 'Dashboard',  source: 'dashboard',    renderMode: 'cosmetic'                                       },
        contacts:   { icon: 'Kanban',          label: 'Suivi',      source: 'crm',          renderMode: 'specialized', component: 'CRMCreativePanel'     },
        projets:    { icon: 'FolderKanban',    label: 'Projets',    source: 'protocoles',   renderMode: 'specialized', component: 'CreativeProjectPanel'  },
        profils:    { icon: 'User',            label: 'Profils',    source: 'inscriptions', renderMode: 'cosmetic'                                       },
        collectifs: { icon: 'Users',           label: 'Collectifs', source: 'cohortes',     renderMode: 'cosmetic'                                       },
        portfolio:  { icon: 'Image',           label: 'Portfolio',  source: 'portfolio',    renderMode: 'specialized', component: 'CreativePortfolioPanel'},
        staff:      { icon: 'UserCheck',       label: 'Équipe',     source: 'personnel',    renderMode: 'cosmetic'                                       },
        messagerie: { icon: 'MessageSquare',   label: 'Messagerie', source: 'contact',      renderMode: 'cosmetic'                                       },
        stats:      { icon: 'BarChart',        label: 'Métriques',  source: 'stats',        renderMode: 'cosmetic'                                       },
        modération: { icon: 'Shield',          label: 'Modération', source: 'modération',   renderMode: 'cosmetic'                                       },
      },
    },

    vocabulary: {
      learners:        'Talents',
      learner:         'Talent',
      courses:         'Projets',
      course:          'Projet',
      cohortes:        'Collectifs',
      cohorte:         'Collectif',
      staff:           'Équipe interne',
      staffMember:     'Collaborateur',
      enroll:          'Engager',
      complete:        'Livrer',
      progress:        'Avancement',
      sectionLearners: 'TALENTS',
      sectionCourses:  'PROJETS',
      sectionCohortes: 'COLLECTIFS',
      sectionStaff:    'ÉQUIPE',
      sectionCRM:      'SUIVI',
      contact:         'Contact',
      contacts:        'Contacts',
      pipeline:        'Pipeline de production',
      deal:            'Projet signé',
      company:         'Structure',
    },

    kpis: {
      total:      'Talents suivis',
      active:     'Projets en cours',
      revenue:    'CA généré',
      completion: 'Taux de livraison',
    },

    crmConfig: {
      pipelineStages: [
        { id: 'prospect',    label: 'Prospect',      color: 'zinc',    icon: '👀' },
        { id: 'brief',       label: 'Brief reçu',    color: 'sky',     icon: '📋' },
        { id: 'negociation', label: 'Négociation',   color: 'amber',   icon: '🤝' },
        { id: 'production',  label: 'En production', color: 'fuchsia', icon: '🎬' },
        { id: 'livre',       label: 'Livré',         color: 'emerald', icon: '✅' },
        { id: 'perdu',       label: 'Perdu',         color: 'red',     icon: '✗'  },
      ],
      contactFields: ['nom', 'prenom', 'email', 'telephone', 'type_contact', 'specialite', 'portfolio_url', 'budget_estime'],
      scoreLabel:   'Potentiel créatif',
      noteLabel:    'Note de casting / relation',
    },

    sections: {
      dashboard: true, contact: true, protocoles: true, inscriptions: true,
      cohortes: true, personnel: true, branding: true, stats: true,
      modération: true, crm: true,
    },
  },

  // ── 4. SANTÉ / MÉDICAL ────────────────────────────────────────────────────
  health: {
    id:          'health',
    label:       'Santé & Médical',
    description: 'Cliniques, cabinets, formation médicale continue, CHU',
    icon:        '🏥',
    accentColor: 'emerald',

    onboarding: {
      fields: [
        { key: 'profession', label: 'Profession (médecin, IDE, kiné…)', type: 'select', options: ['Médecin', 'Ophtalmologue', 'Infirmier', 'Kinésithérapeute', 'Pharmacien', 'Autre'], required: true },
        { key: 'rpps', label: 'Numéro RPPS / ADELI', type: 'text', required: true },
        { key: 'acceptDPC', label: 'Je m’engage à suivre le parcours DPC et à fournir les justificatifs', type: 'checkbox', required: true },
        { key: 'structureName', label: 'Établissement de santé', type: 'text', required: false },
      ],
      title: 'Informations professionnelles (DPC)',
      description: 'Obligatoire pour l’édition des attestations DPC.',
    },

    personas: {
      label: "Type de structure",
      description: "Sélectionnez votre catégorie pour les attestations DPC et les dossiers ANDPC / FIFPL.",
      required: true,
      tree: [
        {
          key: 'clinique-cabinet',
          label: 'Clinique / Cabinet',
          children: [
            { key: 'cabinet-liberal', label: 'Cabinet libéral' },
            { key: 'clinique-privee', label: 'Clinique privée' },
            { key: 'maison-sante', label: 'Maison de santé pluriprofessionnelle' },
          ]
        },
        {
          key: 'chu-hopital',
          label: 'CHU / Hôpital',
          children: [
            { key: 'chu', label: 'CHU / CHR' },
            { key: 'hopital-public', label: 'Hôpital public' },
            { key: 'ehpad', label: 'EHPAD / USLD' },
          ]
        },
        {
          key: 'professionnel-liberal',
          label: 'Professionnel libéral',
          children: [
            { key: 'medecin', label: 'Médecin' },
            { key: 'ophtalmologue', label: 'Ophtalmologue' },
            { key: 'infirmier', label: 'Infirmier (IDE)' },
            { key: 'kinesitherapeute', label: 'Kinésithérapeute' },
            { key: 'pharmacien', label: 'Pharmacien' },
            { key: 'autre-liberal', label: 'Autre professionnel de santé' },
          ]
        },
        {
          key: 'organisme-dpc',
          label: 'Organisme de DPC',
          children: [
            { key: 'odpc-agree', label: 'ODPC agréé ANDPC' },
            { key: 'simulation-sante', label: 'Centre de simulation en santé' },
          ]
        },
        {
          key: 'autre-health',
          label: 'Autre',
          children: []
        }
      ]
    },

    certificationFramework: {
      primary:   'dpc',                  // Développement Professionnel Continu — spécifique médical
      secondary: ['qualiopi', 'andpc'],
      pdfGenerator: 'DPCPDF',            // générateur dédié DPC
      indicateurs: [
        { code: 'DPC-1',  label: 'Analyse des pratiques professionnelles'       },
        { code: 'DPC-2',  label: 'Acquisition ou approfondissement de connaissances' },
        { code: 'DPC-3',  label: 'Simulation en santé'                          },
        { code: 'QP-IND-2', label: 'Objectifs pédagogiques (Qualiopi)'         },
        { code: 'QP-IND-7', label: 'Évaluation des résultats (Qualiopi)'       },
      ],
      financeurs: [
        { id: 'andpc',   label: 'ANDPC',          description: 'Agence Nationale du DPC — médecins, IDE, kiné…', url: 'https://www.mondpc.fr'          },
        { id: 'fifpl',   label: 'FIFPL',          description: 'Professions libérales de santé',                 url: 'https://www.fifpl.fr'           },
        { id: 'fafpm',   label: 'FAF-PM',         description: 'Médecins libéraux',                              url: 'https://www.fafpm.org'          },
        { id: 'opco_ep', label: 'OPCO EP',        description: 'Établissements privés de santé',                 url: 'https://www.opcoep.fr'          },
        { id: 'anfh',    label: 'ANFH',           description: 'Hôpitaux et établissements publics',             url: 'https://www.anfh.fr'            },
        { id: 'cpf',     label: 'CPF',            description: 'Compte Personnel de Formation',                  url: 'https://www.moncompteformation.gouv.fr' },
      ],
      mentions: 'Ce relevé est établi conformément aux exigences du Développement Professionnel Continu (DPC) '
              + 'et aux critères Qualiopi. Il peut être présenté à l\'ANDPC, aux OPCO du secteur sanitaire '
              + 'et social, ou lors d\'un contrôle de l\'Ordre professionnel compétent.',
      saleArguments: [
        'Parcours éligibles DPC — remboursement ANDPC pour médecins et paramédicaux',
        'Attestations conformes pour renouvellement d\'accréditation ordinale',
        'Financement FIFPL pour les professions libérales de santé',
        'Modules de simulation (PESA) valorisables comme DPC par simulation',
      ],
    },

    layout: {
      homeModule:   'dashboard',
      sidebarOrder: ['dashboard', 'patients', 'protocoles', 'services', 'crm', 'contact', 'praticiens', 'stats', 'branding', 'modération'],
      modules: {
        dashboard:  { icon: 'LayoutDashboard', label: 'Dashboard',            source: 'dashboard',    renderMode: 'cosmetic'                           },
        patients:   { icon: 'Users',           label: 'Patients / Apprenants', source: 'inscriptions', renderMode: 'cosmetic'                           },
        protocoles: { icon: 'BookOpen',        label: 'Protocoles',           source: 'protocoles',   renderMode: 'cosmetic'                           },
        services:   { icon: 'Grid',            label: 'Services / Unités',    source: 'cohortes',     renderMode: 'cosmetic'                           },
        crm:        { icon: 'Kanban',          label: 'Suivi patients',       source: 'crm',          renderMode: 'specialized', component: 'CRMPanel' },
        contact:    { icon: 'MessageSquare',   label: 'Messagerie',           source: 'contact',      renderMode: 'cosmetic'                           },
        praticiens: { icon: 'UserCheck',       label: 'Praticiens',           source: 'personnel',    renderMode: 'cosmetic'                           },
        stats:      { icon: 'BarChart',        label: 'Épidémiologie',        source: 'stats',        renderMode: 'cosmetic'                           },
        branding:   { icon: 'Palette',         label: 'Communication',        source: 'branding',     renderMode: 'cosmetic'                           },
        modération: { icon: 'Shield',          label: 'Conformité / RGPD',    source: 'modération',   renderMode: 'cosmetic'                           },
      },
    },

    vocabulary: {
      learners:        'Patients',
      learner:         'Patient',
      courses:         'Protocoles',
      course:          'Protocole',
      cohortes:        'Services',
      cohorte:         'Service',
      staff:           'Praticiens',
      staffMember:     'Praticien',
      enroll:          'Admettre',
      complete:        'Clôturer',
      progress:        'Suivi',
      sectionLearners: 'PATIENTS',
      sectionCourses:  'PROTOCOLES',
      sectionCohortes: 'SERVICES',
      sectionStaff:    'PRATICIENS',
      sectionCRM:      'SUIVI PATIENTS',
      contact:         'Référent',
      contacts:        'Référents',
      pipeline:        'Parcours de soins',
      deal:            'Dossier patient',
      company:         'Établissement',
    },

    kpis: {
      total:      'Patients suivis',
      active:     'En cours de soin',
      revenue:    'Actes réalisés',
      completion: 'Taux de clôture',
    },

    crmConfig: {
      pipelineStages: [
        { id: 'admission',  label: 'Admission',        color: 'zinc',    icon: '📋' },
        { id: 'diagnostic', label: 'Diagnostic',       color: 'sky',     icon: '🔬' },
        { id: 'traitement', label: 'Traitement',       color: 'amber',   icon: '💊' },
        { id: 'suivi',      label: 'Suivi post-soins', color: 'purple',  icon: '📊' },
        { id: 'cloture',    label: 'Clôturé',          color: 'emerald', icon: '✅' },
        { id: 'perdu',      label: 'Perdu de vue',     color: 'red',     icon: '⚠️' },
      ],
      contactFields: ['nom', 'prenom', 'email', 'telephone', 'date_naissance', 'medecin_referent', 'pathologie'],
      scoreLabel:   'Score de criticité',
      noteLabel:    'Observation clinique',
    },

    sections: {
      dashboard: true, contact: true, protocoles: true, inscriptions: true,
      cohortes: true, personnel: true, branding: true, stats: true,
      modération: true, crm: true,
    },
  },

  // ── 5. CONSEIL / SERVICES ─────────────────────────────────────────────────
  consulting: {
    id:          'consulting',
    label:       'Conseil & Services',
    description: 'Cabinets, ESN, freelances B2B, accompagnement',
    icon:        '💼',
    accentColor: 'purple',

    onboarding: {
      title: 'Informations cabinet (Qualiopi)',
      description: 'Ces données sont requises pour les attestations et le référencement Qualiopi.',
      fields: [
        { key: 'companyName', label: 'Raison sociale', type: 'text', required: true },
        { key: 'siret', label: 'Numéro SIRET', type: 'text', required: false },
        { key: 'acceptQualiopi', label: 'Je déclare respecter les critères Qualiopi', type: 'checkbox', required: true },
        { key: 'speciality', label: 'Domaine d’expertise', type: 'text', required: false },
      ],
    },

    personas: {
      label: "Type de structure",
      description: "Précisez votre activité pour les dossiers FIFPL, AKTO et les appels d'offres.",
      required: true,
      tree: [
        {
          key: 'cabinet-conseil',
          label: 'Cabinet de conseil',
          children: [
            { key: 'conseil-strategie', label: 'Conseil en stratégie' },
            { key: 'conseil-rh', label: 'Conseil RH / Management' },
            { key: 'conseil-juridique', label: 'Conseil juridique / Expertise comptable' },
          ]
        },
        {
          key: 'esn',
          label: 'ESN / Société de services',
          children: [
            { key: 'esn-dev', label: 'Développement & intégration' },
            { key: 'esn-infra', label: 'Infrastructure & Cloud' },
            { key: 'esn-data', label: 'Data & IA' },
          ]
        },
        {
          key: 'freelance-b2b',
          label: 'Freelance B2B',
          children: [
            { key: 'consultant-independant', label: 'Consultant indépendant' },
            { key: 'coach-certifie', label: 'Coach certifié' },
            { key: 'formateur-conseil', label: 'Formateur-conseil' },
          ]
        },
        {
          key: 'accompagnement',
          label: 'Accompagnement',
          children: [
            { key: 'bilan-competences', label: 'Bilan de compétences' },
            { key: 'vae', label: 'VAE / Orientation professionnelle' },
            { key: 'outplacement', label: 'Outplacement / Transition pro' },
          ]
        },
        {
          key: 'autre-consulting',
          label: 'Autre',
          children: []
        }
      ]
    },

    certificationFramework: {
      primary:   'qualiopi',
      secondary: ['pmp', 'iso9001', 'rs'],
      pdfGenerator: 'QualiopiPDF',
      indicateurs: [
        { code: 'IND-1',  label: 'Information et accès au public'               },
        { code: 'IND-2',  label: 'Objectifs et compétences métier'              },
        { code: 'IND-4',  label: 'Moyens pédagogiques et encadrement'           },
        { code: 'IND-7',  label: 'Résultats et satisfaction client'             },
      ],
      financeurs: [
        { id: 'fifpl',   label: 'FIFPL',          description: 'Professions libérales (avocats, experts-comptables…)', url: 'https://www.fifpl.fr'      },
        { id: 'opco_ep', label: 'OPCO EP',        description: 'Cabinets et entreprises de moins de 50 salariés',     url: 'https://www.opcoep.fr'     },
        { id: 'akto',    label: 'AKTO',           description: 'ESN, cabinets conseil, services',                     url: 'https://www.akto.fr'       },
        { id: 'cpf',     label: 'CPF',            description: 'Compte Personnel de Formation',                       url: 'https://www.moncompteformation.gouv.fr' },
        { id: 'fne',     label: 'FNE-Formation',  description: 'Accompagnement transitions et reconversions',         url: 'https://travail-emploi.gouv.fr' },
      ],
      mentions: 'Ce relevé est établi conformément aux exigences Qualiopi. Il peut être utilisé '
              + 'pour justifier des dépenses de formation auprès de l\'URSSAF, du FIFPL '
              + 'ou de tout OPCO compétent dans les secteurs du conseil et des services.',
      saleArguments: [
        'Financement FIFPL pour consultants et professions libérales',
        'Eligible AKTO pour les ESN et cabinets de conseil',
        'Dossiers CPF acceptés — formation finançable sans avance de frais',
        'Attestations certifiantes valorisables dans les appels d\'offres publics',
      ],
    },

    layout: {
      homeModule:   'crm',
      sidebarOrder: ['crm', 'dashboard', 'clients', 'missions', 'equipes', 'contact', 'consultants', 'stats', 'branding', 'modération'],
      modules: {
        crm:         { icon: 'Kanban',          label: 'Pipeline',       source: 'crm',          renderMode: 'specialized', component: 'CRMPanel' },
        dashboard:   { icon: 'LayoutDashboard', label: 'Dashboard',      source: 'dashboard',    renderMode: 'cosmetic'                           },
        clients:     { icon: 'Users',           label: 'Clients',        source: 'inscriptions', renderMode: 'cosmetic'                           },
        missions:    { icon: 'BookOpen',        label: 'Missions',       source: 'protocoles',   renderMode: 'cosmetic'                           },
        equipes:     { icon: 'Grid',            label: 'Équipes projet', source: 'cohortes',     renderMode: 'cosmetic'                           },
        contact:     { icon: 'MessageSquare',   label: 'Messagerie',     source: 'contact',      renderMode: 'cosmetic'                           },
        consultants: { icon: 'UserCheck',       label: 'Consultants',    source: 'personnel',    renderMode: 'cosmetic'                           },
        stats:       { icon: 'BarChart',        label: 'Performance',    source: 'stats',        renderMode: 'cosmetic'                           },
        branding:    { icon: 'Palette',         label: 'Identité',       source: 'branding',     renderMode: 'cosmetic'                           },
        modération:  { icon: 'Shield',          label: 'Conformité',     source: 'modération',   renderMode: 'cosmetic'                           },
      },
    },

    vocabulary: {
      learners:        'Clients',
      learner:         'Client',
      courses:         'Missions',
      course:          'Mission',
      cohortes:        'Équipes projet',
      cohorte:         'Équipe',
      staff:           'Consultants',
      staffMember:     'Consultant',
      enroll:          'Affecter',
      complete:        'Livrer',
      progress:        'Avancement',
      sectionLearners: 'CLIENTS',
      sectionCourses:  'MISSIONS',
      sectionCohortes: 'ÉQUIPES',
      sectionStaff:    'CONSULTANTS',
      sectionCRM:      'PIPELINE',
      contact:         'Contact',
      contacts:        'Contacts',
      pipeline:        'Pipeline commercial',
      deal:            'Affaire',
      company:         'Entreprise cliente',
    },

    kpis: {
      total:      'Clients total',
      active:     'Missions actives',
      revenue:    'CA en cours',
      completion: 'Taux de livraison',
    },

    crmConfig: {
      pipelineStages: [
        { id: 'identification', label: 'Identification', color: 'zinc',    icon: '🔎' },
        { id: 'qualification',  label: 'Qualification',  color: 'sky',     icon: '📊' },
        { id: 'proposition',    label: 'Proposition',    color: 'amber',   icon: '📄' },
        { id: 'negociation',    label: 'Négociation',    color: 'orange',  icon: '🤝' },
        { id: 'gagne',          label: 'Gagné',          color: 'emerald', icon: '🏆' },
        { id: 'perdu',          label: 'Perdu',          color: 'red',     icon: '✗'  },
      ],
      contactFields: ['nom', 'poste', 'email', 'entreprise', 'secteur', 'budget_mission'],
      scoreLabel:   'Score de maturité',
      noteLabel:    'Compte-rendu',
    },

    sections: {
      dashboard: true, contact: true, protocoles: true, inscriptions: true,
      cohortes: true, personnel: true, branding: true, stats: true,
      modération: true, crm: true,
    },
  },
};

export function getProfile(profileId) {
  return PROFILES[profileId] ?? PROFILES.education;
}

export function getCertificationFramework(profileId) {
  return getProfile(profileId).certificationFramework;
}

export function getPdfGenerator(profileId) {
  return getCertificationFramework(profileId)?.pdfGenerator ?? 'QualiopiPDF';
}

export function getFinanceurs(profileId) {
  return getCertificationFramework(profileId)?.financeurs ?? [];
}

export function getSaleArguments(profileId) {
  return getCertificationFramework(profileId)?.saleArguments ?? [];
}

export const PROFILE_LIST = Object.values(PROFILES).map(
  ({ id, label, description, icon, accentColor, certificationFramework }) => ({
    id,
    label,
    description,
    icon,
    accentColor,
    primaryCert: certificationFramework?.primary ?? null,
  })
);

export function generateDemoContacts(profileId = 'education') {
  const profile = getProfile(profileId);
  const stages  = profile.crmConfig.pipelineStages;

  const templates = {
    education: [
      { nom: 'Martin',    prenom: 'Sophie',   email: 'sophie.martin@email.fr',   entreprise: 'Lycée Voltaire',        stage: stages[1]?.id, score: 78 },
      { nom: 'Dubois',    prenom: 'Lucas',    email: 'lucas.dubois@email.fr',    entreprise: 'Université Paris X',    stage: stages[0]?.id, score: 45 },
      { nom: 'Leroy',     prenom: 'Emma',     email: 'emma.leroy@email.fr',      entreprise: 'BTS Commerce',          stage: stages[2]?.id, score: 91 },
      { nom: 'Moreau',    prenom: 'Antoine',  email: 'a.moreau@email.fr',        entreprise: 'Indépendant',           stage: stages[3]?.id, score: 88 },
      { nom: 'Simon',     prenom: 'Chloé',    email: 'chloe.simon@email.fr',     entreprise: 'IUT Lyon',              stage: stages[4]?.id, score: 95 },
      { nom: 'Michel',    prenom: 'Pierre',   email: 'p.michel@email.fr',        entreprise: 'CFA Paris',             stage: stages[5]?.id, score: 22 },
    ],
    corporate: [
      { nom: 'Dupont',    prenom: 'Alice',    email: 'alice@techcorp.io',         entreprise: 'TechCorp SA',           stage: stages[2]?.id, score: 82 },
      { nom: 'Bernard',   prenom: 'Thomas',   email: 'thomas@startup.co',         entreprise: 'StartupCo',             stage: stages[1]?.id, score: 67 },
      { nom: 'Robert',    prenom: 'Marie',    email: 'marie@agency.fr',           entreprise: 'Agency Plus',           stage: stages[3]?.id, score: 74 },
      { nom: 'Richard',   prenom: 'Julien',   email: 'julien@telecom.fr',         entreprise: 'TelecomGroup',          stage: stages[4]?.id, score: 93 },
      { nom: 'Petit',     prenom: 'Léa',      email: 'lea@enterprise.com',        entreprise: 'Enterprise SA',         stage: stages[0]?.id, score: 38 },
      { nom: 'Garcia',    prenom: 'Marc',     email: 'marc@bigco.fr',             entreprise: 'BigCo Industries',      stage: stages[5]?.id, score: 15 },
    ],
    creative: [
      { nom: 'Beaumont',  prenom: 'Jade',     email: 'jade@studio.fr',            entreprise: 'Studio Pixel',          stage: stages[3]?.id, score: 85 },
      { nom: 'Renard',    prenom: 'Hugo',     email: 'hugo@agence.fr',            entreprise: 'Agence Lumière',        stage: stages[1]?.id, score: 70 },
      { nom: 'Mercier',   prenom: 'Inès',     email: 'ines@freelance.fr',         entreprise: '',                      stage: stages[2]?.id, score: 78 },
      { nom: 'Fontaine',  prenom: 'Alexis',   email: 'alexis@prod.fr',            entreprise: 'Prod & Co',             stage: stages[4]?.id, score: 92 },
      { nom: 'Chevalier', prenom: 'Clara',    email: 'clara@media.fr',            entreprise: 'Média Sud',             stage: stages[0]?.id, score: 44 },
      { nom: 'Gauthier',  prenom: 'Romain',   email: 'romain@lost.fr',            entreprise: '',                      stage: stages[5]?.id, score: 18 },
    ],
    health: [
      { nom: 'Leclerc',   prenom: 'Martine',  email: 'm.leclerc@chu.fr',          entreprise: 'CHU Bordeaux',          stage: stages[2]?.id, score: 80 },
      { nom: 'Vidal',     prenom: 'Damien',   email: 'd.vidal@clinique.fr',       entreprise: 'Clinique du Parc',      stage: stages[1]?.id, score: 65 },
      { nom: 'Perrin',    prenom: 'Sylvie',   email: 's.perrin@cabinet.fr',       entreprise: 'Cabinet Santé+',        stage: stages[3]?.id, score: 72 },
      { nom: 'Arnaud',    prenom: 'Nicolas',  email: 'n.arnaud@hopital.fr',       entreprise: 'Hôpital Lariboisière',  stage: stages[0]?.id, score: 55 },
      { nom: 'Blanchard', prenom: 'Émilie',   email: 'e.blanchard@ssiad.fr',      entreprise: 'SSIAD Loire',           stage: stages[4]?.id, score: 90 },
      { nom: 'Roussel',   prenom: 'Frédéric', email: 'f.roussel@perdu.fr',        entreprise: '',                      stage: stages[5]?.id, score: 20 },
    ],
    consulting: [
      { nom: 'Lambert',   prenom: 'Caroline', email: 'c.lambert@cabinet.fr',      entreprise: 'Groupe Audit',          stage: stages[3]?.id, score: 85 },
      { nom: 'Fontaine',  prenom: 'Olivier',  email: 'o.fontaine@conseil.fr',     entreprise: 'Conseil & Co',          stage: stages[2]?.id, score: 71 },
      { nom: 'Rousseau',  prenom: 'Isabelle', email: 'i.rousseau@rh.fr',          entreprise: 'DRH Industrie',         stage: stages[1]?.id, score: 58 },
      { nom: 'Morel',     prenom: 'Stéphane', email: 's.morel@pme.fr',            entreprise: 'PME Locale',            stage: stages[4]?.id, score: 96 },
      { nom: 'Bonnet',    prenom: 'Nathalie', email: 'n.bonnet@finance.fr',       entreprise: 'Finance Corp',          stage: stages[0]?.id, score: 42 },
      { nom: 'Girard',    prenom: 'François', email: 'f.girard@lost.fr',          entreprise: 'Concurrent',            stage: stages[5]?.id, score: 20 },
    ],
  };

  const base = templates[profileId] ?? templates.education;
  return base.map((t, i) => ({
    _id:       `demo_${profileId}_${i}`,
    ...t,
    telephone: `06 ${String(10 + i * 7).padStart(2,'0')} ${String(20 + i * 3).padStart(2,'0')} ${String(30 + i * 11 % 100).padStart(2,'0')} ${String(40 + i * 17 % 100).padStart(2,'0')}`,
    tags:      [],
    notes:     [],
    createdAt: new Date(Date.now() - i * 86400000 * 3).toISOString(),
    updatedAt: new Date(Date.now() - i * 86400000).toISOString(),
  }));
}

export function resolveVocab(profile, token, fallback = token) {
  return profile?.vocabulary?.[token] ?? fallback;
}

export function buildSections(profile, baseSections = []) {
  const layout  = profile.layout;
  const modules = layout?.modules ?? {};
  const order   = layout?.sidebarOrder ?? baseSections.map(s => s.id);

  return order.map(id => {
    const mod  = modules[id];
    const base = baseSections.find(s => s.id === (mod?.source ?? id));
    return {
      id,
      label:      mod?.label      ?? base?.label  ?? id,
      icon:       mod?.icon       ?? base?.icon   ?? 'Circle',
      short:      base?.short     ?? id.slice(0, 3).toUpperCase(),
      color:      base?.color     ?? 'zinc',
      source:     mod?.source     ?? id,
      renderMode: mod?.renderMode ?? 'cosmetic',
      component:  mod?.component  ?? null,
    };
  });
}

export default PROFILES;